import { ApprovedStatus } from '@/models/reservation-approved-status'
import { TransactionSideType } from './smaregi/transaction'

export type PaymentSettingType = {
  id: string
  name: string
  commissionRate: number
  isDefaultCash: boolean
  isFixed: boolean
  methodCode?: string
  type?: number
  isAccountsReceivable?: number | string
  displayOrder?: number
  tax?: number | string
  taxClass?: string
  children?: PaymentSettingType[]
}

// デフォルト項目の設定
export const DiscountType = {
  Custom: 0, // 指定なし
  Default: 1, // ポイント
} as const
type DiscountType = typeof DiscountType[keyof typeof DiscountType]

export type DiscountSettingType = {
  id?: string
  displayOrder: number
  discountSubjectCode: string
  name: string
  price: string
  type: DiscountType
  isAccountsReceivable: number
  accountsReceivableCode: string
  accountsReceivableName: string
  isPaymentAllocation: boolean
}

export type SalesType = {
  id: string
  salesDate: string
  reservationName: string
  salesDepartmentName: string
  salesSubjectId: string
  salesSubjectName: string
  salesSubjectPrice: number | ''
  quantity: number
  salesSubjectTax: number
  salesPaymentId: string
  salesPaymentName: string
  salesPaymentPrice: number
  isPaid: boolean
  salesPaymentCommissionRate: number
  checkinName: string
  isConfirm: boolean | number
  /** 締め処理で固定化（sales.is_confirmed）。GET /sales-manager/sales の sales[]（0/1/null） */
  isConfirmed?: boolean | number | null
  salesPrice: number
  unpaid: number
  paymentSalesManagerDailyId: string
  accommodationId: string
  salesSubSubjectId: string
  salesSubSubjectCode: string
  salesSubSubjectName: string
  salesSubSubjectPrice: number
  reservationId: string
  salesAccountsReceivableMasterId: string
  salesAccountsReceivableName: string
  companyId?: string
  guestRoomAssignId?: string
  guestRoomAssignShortId?: string
  roomNumber?: string
  hasSentPos?: boolean
  transactionSide?: TransactionSideType
  createdAt: string
  salesSubjectCode: string
  isRevise?: boolean
  /**
   * FE専用: 利用明細の編集中フラグ（未変更行は保存対象外）。
   * GET /sales-manager/sales には含まれない。admin-sales 送信時は除去する。
   */
  isEdit?: boolean
  /** 入金日時（Unix 秒）。GET /sales-manager/sales の sales[] */
  paymentAtUnixtime?: number | null
  /** @deprecated paymentAtUnixtime を使用 */
  paymentUnixtime?: number | null
  /** 入金期間の営業日（Y-m-d）。GET /sales-manager/sales の sales[] */
  paymentBusinessSalesDate?: string | null
}

export type EditSalesType = {
  id?: string
  companyId?: string
  salesDate: string
  salesSubjectId: string
  salesSubjectName?: string
  salesSubSubjectId: string
  salesSubSubjectName?: string
  salesSubjectPrice: number | ''
  quantity: number
  salesPaymentId: string
  salesPaymentPrice: number
  isPaid: boolean
  checkinId?: string
  reservationId?: string
  isEdit?: boolean
  paymentSalesManagerDailyId?: string | null
  accommodationId: string
  salesSubjectCode: string
  salesSubSubjectCode: string
  salesAccountsReceivableMasterId: string
  salesAccountsReceivableName: string
  guestRoomAssignId?: string
  salesSubjectSetId?: string
  /** FE専用: 宿泊税プレビュー由来の新規行。税行全削除時に101減額を戻す判定に使う（API非送信） */
  fromAccommodationTax?: boolean
}

export type SalesDiscountType = {
  approvedStatus: typeof ApprovedStatus
  checkinId?: string
  reservationId?: string
  accommodationId?: string
  id: string
  isConfirm: boolean | number
  /** 締め処理で固定化（sales.is_confirmed）。GET /sales-manager/sales の sales[]（0/1/null） */
  isConfirmed?: boolean | number | null
  salesDate: string
  salesDiscountId: string
  salesDiscountName: string
  salesPaymentId: string
  salesPaymentName: string
  salesSubjectPrice: number
  paymentSalesManagerDailyId: string
  salesAccountsReceivableMasterId: string
  salesAccountsReceivableName: string
  companyId?: string
  guestRoomAssignId?: string
  hasSentPos?: boolean
  transactionSide?: TransactionSideType
  roomNumber?: string
  guestRoomAssignShortId?: string
  createdAt: string
  salesSubjectCode: string
  isRevise?: boolean
  paymentAtUnixtime?: number | null
  paymentUnixtime?: number | null
  /** 入金期間の営業日（Y-m-d） */
  paymentBusinessSalesDate?: string | null
  isEdit?: boolean
}

export type EditDiscountType = {
  id?: string
  salesDate: string
  salesDiscountId: string
  salesSubjectPrice: number | ''
  salesPaymentId: string
  checkinId?: string
  reservationId?: string
  isDiscount: boolean
  isEdit?: boolean
  paymentSalesManagerDailyId?: string | null
  salesAccountsReceivableMasterId?: string
  companyId?: string
  guestRoomAssignId?: string
}

export type TotalListType = {
  name: string
  totalPayment: string
}

export type UsageDetailTyoe = {
  confirmDay: string
  sales: SalesType[]
}

// Sales revision
export type SalesReviseType = {
  id?: string
  salesDate: string
  salesSubjectId: string
  salesSubjectName: string
  salesSubjectPrice: string
  salesPaymentId: string
  salesPaymentName: string
  note: string
  isEdit?: boolean
  isConfirm: boolean
  salesManagerDailyId: string
}

// Sales revision (edit)
export type EditSalesReviseType = {
  id?: string
  salesDate: string
  salesSubjectId: string
  salesSubjectPrice: string
  salesPaymentId: string
  note: string | ''
  salesManagerDailyId: string
}

export type CalculatedSalesSubjectSetType = {
  checkinId: string | undefined
  hotelId: string
  guestRoomAssignId: string
  reservationId: string
  accommodationId: string
  guestRoomId: string
  pax: number
  salesSubjectId: string
  asSeparated: string
  salesSubjectCode: string
  salesSubjectName: string
  salesSubjectPrice: number
  salesSubjectTax: number
  salesSubSubjectId: string | undefined
  salesSubSubjectName: string | undefined
  salesSubSubjectCode: string | undefined
  departmentCode: string
  departmentName: string
  quantity: number
  salesDate: string
  salesPaymentPrice: number
  isDisplay: boolean
}

/** GET /sales-manager/sales-account-subject-tax の計算プレビュー明細（必要最小限） */
export type CalculatedSalesSubjectTaxType = {
  action: 'create' | 'update'
  /** action=update 時の既存室料(101)明細ID */
  id?: string
  checkinId?: string
  reservationId?: string
  /** create 時。reservation.accommodation_id 由来 */
  accommodationId?: string
  guestRoomAssignId?: string
  salesDate?: string
  salesSubjectId?: string
  salesSubjectName?: string
  salesSubjectCode?: string
  salesSubjectPrice: number
  quantity: number
  salesPaymentPrice?: number
  /** プランに含む × 室料精算済 の create のみ。支払い方法マスタID */
  salesPaymentId?: string
  /** プランに含む × 室料精算済 の create のみ。売掛先が無いときはキー自体なし */
  salesAccountsReceivableMasterId?: string
  /** 紐付けキー（BE内部／互換）。FEの明細組み立てには未使用 */
  sourceRoomChargeSalesId?: string
  /** 任意。FEでは未使用（減額前は既存明細を backup） */
  previousSalesSubjectPrice?: number
}
